/**
 * Per-device backend registry.
 *
 * Since the 2026-09-01 cutover every request — including the device notify
 * WebSocket — goes to the Pro compat layer, which relays mDash-backed devices
 * through a Durable Object that cannot hibernate while a socket is held open.
 * That relay is the account's only metered cost, and it is pure overhead for
 * devices that still live in mDash: mdash.net accepts the notify socket
 * directly (any Origin; WebSockets are not CORS-gated), exactly as this app
 * did before the cutover.
 *
 * The compat layer tells the client which backend served a device with the
 * response header `X-Kegtron-Backend: mdash | supadash` on `GET /api/v2/m/device`
 * (exposed via Access-Control-Expose-Headers). This module records that answer
 * so websocketService can open the notify socket against the right origin.
 *
 * REST is unaffected — it always goes to API_ORIGIN. Only the WebSocket host
 * is chosen from this registry.
 *
 * Deliberately NOT persisted: a device can migrate from mDash to supaDash at
 * any time, so the mapping is re-learned from response headers each session and
 * a stale value can never outlive the tab. An unknown pubkey falls back to
 * API_ORIGIN, which is correct on both backends (it just costs).
 *
 * Keep this module side-effect free and free of DOM/app dependencies — it is
 * imported by services that the service worker compilation can reach.
 */

/** Backend identifiers the compat layer may report. */
export const BACKEND_MDASH = 'mdash';
export const BACKEND_SUPADASH = 'supadash';

/** Response header carrying the backend that served a device request. */
export const BACKEND_HEADER = 'x-kegtron-backend';

const VALID_BACKENDS = [BACKEND_MDASH, BACKEND_SUPADASH];

/**
 * pubkey → backend. A Map is used deliberately: this is a session-scoped
 * lookup cache, not application data, and the device fan-out writes to it once
 * per device per poll on fleets of several hundred devices.
 */
const registry = new Map();

/**
 * Record the backend serving a device.
 * Unknown/blank values are ignored so a malformed header can never downgrade a
 * previously-learned mapping.
 * @param {string} pubkey - Device public key (access token)
 * @param {string} backend - 'mdash' or 'supadash'
 * @returns {void}
 */
export function setBackend(pubkey, backend) {
  if (!pubkey || typeof backend !== 'string') return;

  const normalized = backend.trim().toLowerCase();
  if (!VALID_BACKENDS.includes(normalized)) return;

  registry.set(pubkey, normalized);
}

/**
 * Look up the backend serving a device.
 * @param {string} pubkey - Device public key
 * @returns {('mdash'|'supadash'|undefined)} undefined when not yet learned
 */
export function getBackend(pubkey) {
  if (!pubkey) return undefined;
  return registry.get(pubkey);
}

/**
 * Read the backend header off an axios response header bag.
 * Handles both the plain object and the axios v1 AxiosHeaders instance.
 * @param {object} headers - `res.headers`
 * @returns {string|undefined}
 */
function readBackendHeader(headers) {
  if (!headers) return undefined;

  const direct = headers[BACKEND_HEADER];
  if (typeof direct === 'string') return direct;

  if (typeof headers.get === 'function') {
    const viaGetter = headers.get(BACKEND_HEADER);
    if (typeof viaGetter === 'string') return viaGetter;
  }

  return undefined;
}

/**
 * Learn a device's backend from an axios response.
 * A response without the header leaves the registry untouched (the backend
 * stays unknown, and the caller falls back to API_ORIGIN).
 * @param {string} pubkey - Device public key the request was made for
 * @param {object} res - Axios response
 * @returns {void}
 */
export function recordBackendFromResponse(pubkey, res) {
  if (!pubkey || !res) return;
  setBackend(pubkey, readBackendHeader(res.headers));
}

/**
 * Drop every learned mapping. Used by tests; also safe to call on sign-out,
 * though not currently wired there — pubkeys are per-device, so a different
 * account looks up different keys and cannot inherit this routing anyway.
 * @returns {void}
 */
export function clearBackends() {
  registry.clear();
}
